'use client';
import {
  SearchAnalyticsBoundary,
  SearchAnalyticsItem,
  type SearchAnalyticsData,
} from '@eliadmin/core/src/components/analytics/search-analytics-boundary';
import { SearchSort } from '@eliadmin/core/src/components/header/search-bar/search-sort';
import { searchSortOptions } from '@eliadmin/core/src/utils/search-sort';

import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { SearchFields } from '@eliadmin/core/src/utils/product-utils';
import {
  buildProductsQuery,
  parseProductSearchParams,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Suspense, useMemo, useState, useEffect } from 'react';
import { DEFAULT_SEARCH_FIELDS } from '@eliadmin/core/src/components/header/search-bar/custom-search-config';
import { CustomBreadcrumbs } from '../../components/ui/custom-breadcrumbs';
import { Heading } from '../../components/ui/heading';

export default function SearchPage() {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();

  const searchParams = useSearchParams();

  const parsed = useMemo(() => parseProductSearchParams(searchParams), [searchParams]);

  const [usedCustomSearchFields, setUsedCustomSearchFields] = useState<SearchFields | undefined>(
    () => parsed.search_fields ?? DEFAULT_SEARCH_FIELDS,
  );

  // Keep local "used" fields in sync with URL
  useEffect(() => {
    // When URL contains explicit search_fields, prefer them.
    // Otherwise fall back to configured defaults.
    if (parsed.search_fields) {
      setUsedCustomSearchFields(parsed.search_fields);
    } else {
      setUsedCustomSearchFields(DEFAULT_SEARCH_FIELDS);
    }
  }, [parsed.search_fields]);

  // legacy sync function removed; handled directly in onApply

  const query = buildProductsQuery({
    ...parsed,
    search_fields: usedCustomSearchFields ?? parsed.search_fields,
    results: 12,
    showAttributes: true,
    ...searchSortOptions(searchParams),
  });

  const productRequestUri = buildProductsRequest(query).uri;
  const productResetUri = buildProductsRequest({ ...query, page: undefined }).uri;

  const {
    data: products,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [CacheKey.PRODUCTS, locale, productRequestUri, me?.authToken ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        query,
        authToken: me?.authToken,
      }),
    enabled: isInitialized,
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <section className={cn('custom-section', 'gap-8 py-8 sm:gap-10 sm:py-24 lg:gap-12 lg:py-32')}>
        <div
          className={cn(
            'custom-container',
            'flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6',
          )}
        >
          <CustomBreadcrumbs breadcrumbs={[{ name: searchParams.get('search') ?? '', href: '' }]} />
          <Heading title={searchParams.get('search') ?? ''} tag="h1" />
        </div>
        <div className={cn('custom-container', 'flex flex-col items-start justify-start')}>
          <div className="flex flex-col items-center gap-4">
            {isLoading ? (
              <div className={cn('flex h-[300px] w-full items-center justify-center')}>
                <Spinner />
              </div>
            ) : (
              <>
                {!!products?.data?.length && (
                  <TrackComponent
                    eventName={'view_item_list'}
                    eventParams={{
                      item_list_id: `search:${searchParams.get('search') ?? ''}`,
                      item_list_name: 'search',
                    }}
                    items={products.data}
                  />
                )}
                <>
                  <SearchSort />
                  <SearchAnalyticsBoundary
                    mode="submitted"
                    active={!isFetching}
                    data={{
                      manifest: products?.meta?.search,
                      identity: JSON.stringify([
                        locale,
                        buildProductsRequest(query).uri,
                        me?.customer.id ?? null,
                      ]),
                      locale,
                      results: (products?.data ?? []).map((product) => ({
                        type: 'product',
                        id: product.id,
                        href: product.slug,
                      })),
                      offset: products?.meta?.from
                        ? products.meta.from - 1
                        : (Number(query.page ?? 1) - 1) * Number(query.results ?? 12),
                    }}
                  >
                    <div
                      className={cn(
                        'grid w-full grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
                      )}
                    >
                      {products?.data.map((product, searchIndex) => (
                        <SearchAnalyticsItem index={searchIndex} key={searchIndex}>
                          {<ProductCard key={`product-${product.id}`} product={product} />}
                        </SearchAnalyticsItem>
                      ))}
                    </div>
                  </SearchAnalyticsBoundary>
                </>
              </>
            )}
            <Suspense>
              <Pagination links={products?.meta?.links ?? []} resetWhen={[productResetUri]} />
            </Suspense>
          </div>
        </div>
      </section>
    </>
  );
}
